import React, { useState, useEffect } from 'react';
import { Typography, createStyles, makeStyles, Button } from '@material-ui/core';
import QRCode from 'qrcode.react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  IOSSwitch,
  ResponsiveContainer,
  useUserKey,
  useGuestRooms,
  useFormEvents,
  ErrorDisplay,
  useObservable,
  Feedback,
  LoadingIndicator,
  useCurrentStay,
  usePropertyGroup,
} from '@lib/common';
import {
  AccountForms,
  accountService,
  FormStatus,
  guestProfileQuery,
  guestProfileService,
  GuestProfilesForms,
  SleepSchedule,
  userStaysService,
} from '@lib/state';
import { GuestPreferredTemperature } from '../components/guest-preferred-temperature';

interface UserKeyStyleProps {
  radius: string;
}

const useStyles = makeStyles(theme =>
  createStyles({
    page: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: '16px',
    },
    card: {
      maxWidth: '360px',
      border: `1px solid ${theme.palette.grey[300]}`,
      borderRadius: ({ radius }: UserKeyStyleProps) => radius,

      '& div': {
        display: 'flex',
        justifyContent: 'center',
      },
    },
    cardHeader: {
      color: theme.palette.primary.dark,
      background: theme.palette.grey[300],
      borderRadius: ({ radius }: UserKeyStyleProps) => `${radius} ${radius} 0 0`,
      padding: '10px',
    },
    cardFooter: {
      color: theme.palette.primary.dark,
      background: theme.palette.grey[300],
      borderRadius: ({ radius }: UserKeyStyleProps) => `0 0 ${radius} ${radius}`,
      padding: '5px',
    },
    roomNumber: {
      justifyContent: 'center',
      alignItems: 'center',
    },
    roomNumberTitle: {
      marginRight: '10px',
    },
    qrCode: {},
    logo: {
      marginBottom: ({ radius }: UserKeyStyleProps) => radius,

      '& img': {
        height: '76px',
        width: 'auto',
      },
    },
    afterCard: {
      marginTop: '24px',
    },
    buttonIcon: {
      marginRight: '10px',
    },
    doNotDisturb: {
      justifyContent: 'center',
      alignItems: 'center',
    },
    setSleepSchedule: {
      justifyContent: 'center',
      alignItems: 'center',
    },
    preferredTemperature: {
      justifyContent: 'center',
      alignItems: 'center',
      '& form': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        whiteSpace: 'nowrap',
      },
      '& form > span:first-child': { marginRight: theme.spacing(1) },
      '& form > .MuiFormControl-root': {
        width: '96px',
        order: 1,
      },
      '& form > p': { order: 1 },
      '& form > button': { order: 2 },
    },
  })
);

export const UserKeyPage: React.FC = () => {
  const styles = useStyles({ radius: '16px' });
  const userKey = useUserKey({
    withStays: true,
  });
  const roomNumbers = useGuestRooms();
  const { currentStay, loading: staysLoading } = useCurrentStay();

  const propertyGroup = usePropertyGroup(currentStay?.property.id);

  const [isNight, setIsNight] = useState(currentStay?.sleepSchedule === SleepSchedule.NightSleeper);

  const [{ status: doNotDisturbStatus, error: doNotDisturbError }] = useFormEvents(
    GuestProfilesForms.UpdateDoNotDisturb
  );
  const [{ status: regenerateStatus, error: regenerateError }, reset] = useFormEvents(
    AccountForms.RegenerateUserKey
  );
  const doNotDisturb = useObservable(guestProfileQuery.isDoNotDisturbOn);
  const guest = useObservable(guestProfileQuery.guest);

  const [showRoomNumber, setShowRoomNumber] = useState(false);
  const roomNumberText = showRoomNumber ? roomNumbers.join(', ') : 'XXXXX';
  const [disableForm, setDisableForm] = useState(false);

  const handleShowRoomNumber = (_: React.ChangeEvent<HTMLInputElement>) => {
    setShowRoomNumber(!showRoomNumber);
  };

  useEffect(() => {
    guestProfileService.getDoNotDisturbStatus();
    guestProfileService.getGuestProfile();
  }, []);

  useEffect(() => {
    setDisableForm(true);
    const handle = setTimeout(() => {
      setDisableForm(false);
    }, 2000);
    return () => clearTimeout(handle);
  }, [doNotDisturb, isNight, setDisableForm]);

  useEffect(() => {
    setIsNight(currentStay?.sleepSchedule === SleepSchedule.NightSleeper);
  }, [currentStay]);

  const handleDoNotDisturb = (event: React.ChangeEvent<HTMLInputElement>) => {
    guestProfileService.setDoNotDisturb(event.target.checked);
  };

  const handleSleepScheduleChange = () => {
    if (!currentStay) return;
    userStaysService.toggleSleepSchedule(
      currentStay,
      currentStay.sleepSchedule === SleepSchedule.DaySleeper
        ? SleepSchedule.NightSleeper
        : SleepSchedule.DaySleeper
    );
  };

  if (!userKey) return null;

  if (regenerateStatus === FormStatus.Pending)
    return <LoadingIndicator loadingText="Updating User Key..." />;

  if (disableForm) return <LoadingIndicator />;

  return (
    <ResponsiveContainer>
      <div className={styles.page}>
        <Feedback show={regenerateStatus === FormStatus.Success} severity="success" onHide={reset}>
          Your Key has been updated.
        </Feedback>
        <ErrorDisplay error={regenerateError} onCancel={reset} />
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <Typography variant="h5">
              <FontAwesomeIcon icon={['far', 'key']} /> My Key
            </Typography>
          </div>
          <div className={styles.qrCode}>
            <QRCode value={userKey} size={300} includeMargin={true} />
          </div>
          {roomNumbers.length > 0 ? (
            <>
              <div className={styles.roomNumber}>
                <Typography variant="subtitle1" className={styles.roomNumberTitle}>
                  Room:
                </Typography>
                <Typography variant="subtitle1">{roomNumberText}</Typography>
                <IOSSwitch checked={showRoomNumber} onChange={handleShowRoomNumber} />
              </div>
              <div className={styles.doNotDisturb}>
                <Typography variant="subtitle1">Do Not Disturb</Typography>
                <IOSSwitch
                  name="doNotDisturb"
                  checked={doNotDisturb ?? false}
                  disabled={doNotDisturbStatus === FormStatus.Pending || disableForm}
                  onChange={handleDoNotDisturb}
                />
              </div>
              {currentStay?.sleepSchedule && (
                <div className={styles.setSleepSchedule}>
                  <Typography variant="subtitle1">Night Sleeper</Typography>
                  <IOSSwitch
                    name="sleepSchedule"
                    checked={!isNight}
                    disabled={staysLoading || disableForm}
                    onChange={handleSleepScheduleChange}
                  />
                  <Typography variant="subtitle1">Day Sleeper</Typography>
                </div>
              )}
              {guest && currentStay && (
                <GuestPreferredTemperature
                  className={styles.preferredTemperature}
                  preferredTemperature={guest.preferredTemperature}
                  defaultTemp={propertyGroup?.defaultTemperature}
                  minTemp={propertyGroup?.minTemperature}
                  maxTemp={propertyGroup?.maxTemperature}
                />
              )}

              <div>
                <ErrorDisplay error={doNotDisturbError} />
              </div>
            </>
          ) : (
            <div className={styles.roomNumber}>
              <Typography variant="h6">Not Checked In</Typography>
            </div>
          )}
          <div className={styles.cardFooter}>
            <Button
              name="changeKey"
              disabled={disableForm}
              onClick={() => accountService.regenerateKey()}
            >
              Change My Key
            </Button>
          </div>
        </div>
      </div>
    </ResponsiveContainer>
  );
};
